export type ShuffleSeed = number | string;

function hashStringSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

function normalizeSeed(seed: ShuffleSeed): number {
  return typeof seed === 'string' ? hashStringSeed(seed) : seed;
}

function mulberry32(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function shuffleArray<T>(array: T[], seed?: ShuffleSeed): T[] {
  const newArray = [...array];
  const rng = seed !== undefined
    ? mulberry32(normalizeSeed(seed))
    : Math.random;
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
}

export type AnswerShuffleSeedSource = {
  question_id: number;
  edition_item_id?: string | null;
  answer_order?: readonly number[] | null;
  answer_order_seed?: ShuffleSeed;
};

export function getAnswerShuffleSeed(
  question: AnswerShuffleSeedSource,
): ShuffleSeed {
  if (question.answer_order_seed !== undefined) {
    return question.answer_order_seed;
  }
  const editionItemId = question.edition_item_id?.trim();
  return editionItemId ? `edition-item:${editionItemId}` : question.question_id;
}

export interface Answerable {
  key: string;
  distractor_1: string;
  distractor_2: string;
  distractor_3: string;
}

export const createAnswersWithIndices = (question: Answerable) => {
  return [
    { text: question.key, index: 0 },
    { text: question.distractor_1, index: 1 },
    { text: question.distractor_2, index: 2 },
    { text: question.distractor_3, index: 3 },
  ];
};

export const createDisplayAnswersWithIndices = (
  question: Answerable & AnswerShuffleSeedSource,
) => {
  const answers = createAnswersWithIndices(question);
  const frozenOrder = question.answer_order;
  if (
    frozenOrder?.length === answers.length &&
    new Set(frozenOrder).size === answers.length &&
    frozenOrder.every((index) =>
      Number.isInteger(index) && index >= 0 && index < answers.length
    )
  ) {
    return frozenOrder.map((index) => answers[index]);
  }

  return shuffleArray(answers, getAnswerShuffleSeed(question));
};
